<div class="content-info vm-page">
  <div class="content-title">
    <div>
      <h3 class="title">Vendor Mapping</h3>
      <p class="vm-sub">Filter products by brand, select them, and set their vendor and vendor margin in one go.</p>
    </div>
    <div class="title-actions">
      <label class="zf-search" title="Search by SKU or name">
        <i class="bi bi-search"></i>
        <input type="text" [(ngModel)]="search" (input)="onSearchInput()" placeholder="Search SKU or name" />
      </label>
    </div>
  </div>

  <!-- Filters -->
  <div class="zf-toolbar vm-filters">
    <div class="zf-toolbar-field vm-f-wide">
      <label>Brand</label>
      <ng-select [items]="brands()" bindLabel="name" bindValue="id" [multiple]="true"
        [searchFn]="brandSearch" [(ngModel)]="brandIds" (ngModelChange)="onFilterChange()"
        placeholder="All brands" notFoundText="No brand found" appendTo="body">
        <ng-template ng-option-tmp let-item="item">
          {{ item.name }}@if (item.manufacturer?.name) {<span class="vm-opt-sub"> · {{ item.manufacturer.name }}</span>}
        </ng-template>
      </ng-select>
    </div>
    <div class="zf-toolbar-field">
      <label>Manufacturer</label>
      <ng-select [items]="manufacturers()" bindLabel="name" bindValue="id" [multiple]="true"
        [(ngModel)]="manufacturerIds" (ngModelChange)="onFilterChange()" placeholder="All manufacturers" appendTo="body">
      </ng-select>
    </div>
    <div class="zf-toolbar-field">
      <label>Department</label>
      <ng-select [items]="departments()" bindLabel="name" bindValue="id" [multiple]="true"
        [(ngModel)]="departmentIds" (ngModelChange)="onFilterChange()" placeholder="All departments" appendTo="body">
      </ng-select>
    </div>
    <div class="zf-toolbar-field">
      <label>Current vendor</label>
      <ng-select [items]="vendors()" bindLabel="name" bindValue="id" [clearable]="true"
        [(ngModel)]="currentVendorId" (ngModelChange)="onFilterChange()" placeholder="Any vendor" appendTo="body">
      </ng-select>
    </div>
    <div class="zf-toolbar-field vm-f-auto">
      <label>Vendor status</label>
      <div class="zf-seg zf-seg-sm" role="radiogroup" aria-label="Vendor status">
        <button type="button" class="zf-seg-opt" [class.on]="vendorState() === 'ALL'" (click)="setVendorState('ALL')">All</button>
        <button type="button" class="zf-seg-opt" [class.on]="vendorState() === 'NONE'" (click)="setVendorState('NONE')">No vendor</button>
        <button type="button" class="zf-seg-opt" [class.on]="vendorState() === 'HAS'" (click)="setVendorState('HAS')">Has vendor</button>
      </div>
    </div>
    @if (hasFilter) {
      <button type="button" class="vm-link vm-clear" (click)="clearFilters()">Clear filters</button>
    }
  </div>

  <!-- Count + selection banner -->
  <div class="vm-countbar">
    <span><strong>{{ total() | number }}</strong> product{{ total() === 1 ? '' : 's' }} match</span>
    @if (withoutVendor()) {
      <span class="vm-pill warn">{{ withoutVendor() | number }} without vendor</span>
    }
    @if (selectedCount()) {
      <span class="vm-sep"></span>
      <span><strong>{{ selectedCount() | number }}</strong> selected</span>
      @if (!allMatching() && pageAllChecked() && total() > rows().length) {
        <button type="button" class="vm-link" (click)="selectAllMatching()">Select all {{ total() | number }} matching</button>
      }
      @if (allMatching()) {
        <span class="vm-dim">(all matching the filter)</span>
      }
      <button type="button" class="vm-link" (click)="clearSelection()">Clear selection</button>
    } @else if (total() && !loading()) {
      <button type="button" class="vm-link" (click)="selectAllMatching()">Select all {{ total() | number }}</button>
    }
  </div>

  <!-- Products -->
  <div class="common-table">
    <div class="table-responsive vm-table-wrap">
      <table class="table vm-table">
        <thead>
          <tr>
            <th class="vm-ck">
              <input type="checkbox" class="form-check-input" [checked]="pageAllChecked()"
                [disabled]="!rows().length" (change)="togglePage()" title="Select this page" />
            </th>
            <th>SKU</th>
            <th>Product</th>
            <th>Brand</th>
            <th>Manufacturer</th>
            <th>Vendor</th>
            <th class="zf-num">Margin %</th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="7" class="text-center vm-dim">Loading products…</td></tr>
          } @else if (!rows().length) {
            <tr>
              <td colspan="7">
                <div class="zf-empty">
                  <strong>No products match</strong>
                  <span>Change the brand or other filters above.</span>
                </div>
              </td>
            </tr>
          } @else {
            @for (r of rows(); track r.id) {
              <tr [class.vm-on]="isChecked(r)" (click)="toggleRow(r)">
                <td class="vm-ck" (click)="$event.stopPropagation()">
                  <input type="checkbox" class="form-check-input" [checked]="isChecked(r)" (change)="toggleRow(r)" />
                </td>
                <td class="zf-mono zf-strong">{{ r.sku }}</td>
                <td>
                  {{ r.longDescription || r.name }}
                  @if (!r.isActive) { <span class="vm-pill">Inactive</span> }
                </td>
                <td>{{ r.brand?.name || '–' }}</td>
                <td class="zf-dim">{{ r.manufacturer?.name || '–' }}</td>
                <td>
                  @if (r.vendor) {
                    {{ r.vendor.name }}
                    @if (r.supplierCount > 1) { <span class="vm-dim" [title]="r.supplierCount + ' mapped suppliers'">+{{ r.supplierCount - 1 }}</span> }
                  } @else {
                    <span class="vm-pill warn">No vendor</span>
                  }
                </td>
                <td class="zf-num zf-mono">{{ r.marginPercentage != null ? (r.marginPercentage | number: '1.0-2') : '–' }}</td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  </div>

  @if (pageCount() > 1) {
    <div class="vm-pager">
      <span class="vm-dim">{{ rangeText }}</span>
      <button type="button" class="btn btnsecondary btn-sm" [disabled]="page() <= 1 || loading()" (click)="load(page() - 1)">
        <i class="bi bi-chevron-left"></i> Prev
      </button>
      <span class="zf-mono">{{ page() }} / {{ pageCount() }}</span>
      <button type="button" class="btn btnsecondary btn-sm" [disabled]="page() >= pageCount() || loading()" (click)="load(page() + 1)">
        Next <i class="bi bi-chevron-right"></i>
      </button>
    </div>
  }

  <!-- Action bar -->
  @if (canEdit) {
  <div class="vm-actionbar" [class.vm-live]="selectedCount() > 0">
    <div class="vm-ab-count">
      <span class="n">{{ selectedCount() | number }}</span>
      <span class="k">selected</span>
    </div>
    <div class="zf-toolbar-field vm-ab-vendor">
      <label>Set vendor</label>
      <ng-select [items]="vendors()" bindLabel="name" bindValue="id" [clearable]="true" [(ngModel)]="vendorId"
        placeholder="Keep current vendor" appendTo="body" dropdownPosition="top">
      </ng-select>
    </div>
    <div class="zf-toolbar-field vm-f-auto">
      <label>As</label>
      <div class="zf-seg zf-seg-sm" role="radiogroup" aria-label="Assign as">
        <button type="button" class="zf-seg-opt" [class.on]="mode() === 'PRIMARY'" [disabled]="!vendorId" (click)="setMode('PRIMARY')"
          title="Becomes the product's vendor (used for POs)">Primary vendor</button>
        <button type="button" class="zf-seg-opt" [class.on]="mode() === 'ADDITIONAL'" [disabled]="!vendorId" (click)="setMode('ADDITIONAL')"
          title="Added as another supplier; the primary vendor is set only where empty">Additional supplier</button>
      </div>
    </div>
    <div class="zf-toolbar-field vm-ab-margin">
      <label>Vendor margin %</label>
      <input type="number" class="form-control" [class.is-invalid]="!marginValid" [(ngModel)]="margin"
        min="0" max="100" step="0.01" placeholder="Keep current" />
    </div>
    <button type="button" class="btn btnprimary text-nowrap" [disabled]="!canApply" (click)="openConfirm()">
      Review &amp; apply
    </button>
  </div>
  }
</div>

<!-- Confirm / preview -->
@if (confirmOpen()) {
  <div class="zf-overlay" (click)="closeConfirm()">
    <div class="zf-dialog w-600" (click)="$event.stopPropagation()">
      <div class="zf-dialog-head">
        <h3>Update {{ selectedCount() | number }} product{{ selectedCount() === 1 ? '' : 's' }}</h3>
        <button type="button" class="zf-x" aria-label="Close" (click)="closeConfirm()">✕</button>
      </div>

      <div class="zf-dialog-body">
        <div class="vm-changes">
          @if (vendorId) {
            <div class="vm-change">
              <span class="zf-dialog-k">Vendor</span>
              <span><strong>{{ vendorName }}</strong> as {{ mode() === 'PRIMARY' ? 'primary vendor' : 'additional supplier' }}</span>
            </div>
          }
          @if (margin != null) {
            <div class="vm-change">
              <span class="zf-dialog-k">Vendor margin</span>
              <span><strong>{{ margin | number: '1.0-2' }}%</strong></span>
            </div>
          }
        </div>

        @if (vendorId && mode() === 'PRIMARY') {
          <div class="vm-opts">
            <label class="vm-opt">
              <input type="checkbox" class="form-check-input" [(ngModel)]="onlyWithoutVendor" (ngModelChange)="refreshPreview()" />
              <span>
                <strong>Only products without a vendor</strong>
                <small>Leave products that already have another vendor as they are.</small>
              </span>
            </label>
            @if (!onlyWithoutVendor) {
              <label class="vm-opt">
                <input type="checkbox" class="form-check-input" [(ngModel)]="dropPreviousVendor" (ngModelChange)="refreshPreview()" />
                <span>
                  <strong>Remove the replaced vendor's mapping</strong>
                  <small>The old vendor stops seeing these SKUs on purchase orders. Untick to keep it as an extra supplier.</small>
                </span>
              </label>
            }
          </div>
        }

        @if (previewLoading()) {
          <div class="vm-dim">Checking the selection…</div>
        } @else if (preview(); as p) {
          <div class="vm-stats">
            <div class="vm-stat"><span class="n">{{ p.withoutVendor | number }}</span><span class="k">without vendor</span></div>
            @if (vendorId) {
              <div class="vm-stat"><span class="n">{{ p.alreadyThisVendor | number }}</span><span class="k">already {{ vendorName }}</span></div>
            }
            <div class="vm-stat" [class.warn]="p.otherVendor && vendorId && mode() === 'PRIMARY' && !onlyWithoutVendor">
              <span class="n">{{ p.otherVendor | number }}</span><span class="k">{{ vendorId ? 'with another vendor' : 'with a vendor' }}</span>
            </div>
          </div>

          @if (vendorId && p.currentVendors.length) {
            <div class="vm-current">
              <span class="zf-dialog-k">Current vendors of the others</span>
              <div class="vm-chips">
                @for (v of p.currentVendors.slice(0, 12); track v.vendorId) {
                  <span class="zf-sum-chip"><span class="k">{{ v.count | number }}</span>{{ v.name }}</span>
                }
                @if (p.currentVendors.length > 12) { <span class="vm-dim">+{{ p.currentVendors.length - 12 }} more</span> }
              </div>
            </div>
          }

          @if (p.willReplace) {
            <div class="zf-alert warn">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <span>{{ p.willReplace | number }} product{{ p.willReplace === 1 ? '' : 's' }} will move from their current vendor to {{ vendorName }}.</span>
            </div>
          }
          @if (p.willSkip) {
            <div class="zf-alert info">
              <i class="bi bi-info-circle-fill"></i>
              <span>{{ p.willSkip | number }} product{{ p.willSkip === 1 ? '' : 's' }} with another vendor will be skipped{{ margin != null ? ' (margin not changed either)' : '' }}.</span>
            </div>
          }
        }
      </div>

      <div class="zf-dialog-foot">
        <span class="note">
          @if (preview(); as p) { {{ p.willUpdate | number }} product{{ p.willUpdate === 1 ? '' : 's' }} will be updated. }
        </span>
        <button type="button" class="btn btnsecondary" (click)="closeConfirm()" [disabled]="applying()">Cancel</button>
        <button type="button" class="btn btnprimary" (click)="apply()"
          [disabled]="applying() || previewLoading() || !preview() || !preview()!.willUpdate">
          {{ applying() ? 'Applying…' : 'Apply' }}
        </button>
      </div>
    </div>
  </div>
}
